import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/navigation/AdminNavigation';

import Button from '../../components/ui/Button';
import ContentLibrary from './components/ContentLibrary';
import ContentEditor from './components/ContentEditor';
import UploadModal from './components/UploadModal';
import BulkActionsBar from './components/BulkActionsBar';
import { contentService } from '../../services/contentService';
import { categoryService } from '../../services/categoryService';
import { useAuth } from '../../contexts/AuthContext';
import { cacheUtils } from '../../utils/cacheUtils';

const ContentManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contentItems, setContentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    type: 'all',
    search: ''
  });
  const [showMobileEditor, setShowMobileEditor] = useState(false);

  // Load content from database
  useEffect(() => {
    loadContent();
    
    // Subscribe to real-time changes
    const unsubscribe = contentService?.subscribeToChanges((payload) => {
      if (payload?.eventType === 'INSERT') {
        setContentItems(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setContentItems(prev => prev?.map(item => 
          item?.id === payload?.new?.id ? payload?.new : item
        ));
      } else if (payload?.eventType === 'DELETE') {
        setContentItems(prev => prev?.filter(item => item?.id !== payload?.old?.id));
      }
    });

    return () => unsubscribe();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const data = await contentService?.getAll(filters);
      setContentItems(data);
    } catch (err) {
      setError(err?.message || 'Erro ao carregar conteúdo');
    } finally {
      setLoading(false);
    }
  };

  // Limpar cache quando delete ou faça upload
  const handleClearCache = async () => {
    try {
      await cacheUtils.clearAppCache();
    } catch (error) {
      console.error('Erro ao limpar cache:', error);
      alert('Erro ao limpar cache. Tente recarregar a página manualmente.');
    }
  };

  // Reload when filters change
  useEffect(() => {
    if (!loading) {
      loadContent();
    }
  }, [filters?.category, filters?.type, filters?.search]);

  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev?.includes(id) 
        ? prev?.filter(itemId => itemId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = (select) => {
    if (select) {
      const allIds = contentItems?.map(item => item?.id);
      setSelectedItems(allIds);
    } else {
      setSelectedItems([]);
    }
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setShowMobileEditor(true);
  };

  const handleSave = (updatedItem) => {
    setContentItems(prev => 
      prev?.map(item => item?.id === updatedItem?.id ? updatedItem : item)
    );
    setSelectedItem(null);
    setShowMobileEditor(false);
  };

  const handleCancel = () => {
    setSelectedItem(null);
    setShowMobileEditor(false);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const handleDeleteSelected = async () => {
    if (selectedItems?.length === 0) return;
    
    try {
      await contentService?.bulkDelete(selectedItems);
      setSelectedItems([]);
      await loadContent(); // Recarregar conteúdo
    } catch (err) {
      setError(err?.message || 'Erro ao deletar itens');
    }
  };

  const handleDeleteItem = async (id) => {
    try {
      await contentService?.delete(id);
      // Update local state to remove the deleted item
      setContentItems(prev => prev?.filter(item => item?.id !== id));
      setSelectedItem(null);
      await loadContent(); // Recarregar conteúdo
    } catch (err) {
      setError(err?.message || 'Erro ao deletar item');
    }
  };

  const handleSaveItem = async (updatedItem) => {
    try {
      if (updatedItem?.id) {
        await contentService?.update(updatedItem?.id, updatedItem);
      } else {
        await contentService?.create(updatedItem);
      }
      setSelectedItem(null);
    } catch (err) {
      setError(err?.message || 'Erro ao salvar item');
    }
  };

  const handleUpload = async ({ files, category, watermarkEnabled, price, format }) => {
    try {
      // Fetch category data to get the UUID
      const categories = await categoryService?.getAll();
      const categoryData = categories?.find(cat => cat?.slug === category);

      if (!categoryData) {
        throw new Error('Categoria não encontrada');
      }

      // Process each uploaded file
      for (const fileData of files) {
        const isVideo = fileData?.type === 'video';
        const fileTitle = fileData?.name?.replace(/\.[^/.]+$/, '');
        
        // Determine format: use manual selection or auto-detected from dimensions
        const contentFormat = format === 'auto' ? fileData?.dimensions?.format : format;
        
        // Use video URL for videos, thumbnail for images
        const videoUrl = isVideo ? fileData?.url : '';
        // Para vídeos, usar thumbnailUrl gerada, senão usar a URL principal
        const thumbnailUrl = isVideo ? (fileData?.thumbnailUrl || fileData?.url) : fileData?.url;
        // Thumbnail é o fallback (salvar em ambos os campos para compatibilidade)
        const thumbnail = thumbnailUrl;

        const newContent = {
          title: fileTitle,
          description: `Conteúdo de ${fileTitle}`,
          category: categoryData?.slug, // Use slug for enum field
          categoryId: categoryData?.id, // Use UUID for foreign key field
          type: isVideo ? 'video' : 'image',
          thumbnail: thumbnail, // Salvar em ambos
          thumbnailAlt: `${isVideo ? 'Vídeo' : 'Imagem'} de ${fileTitle}`,
          thumbnailUrl: thumbnailUrl, // E aqui
          videoUrl,
          price: price || 0,
          watermark: watermarkEnabled,
          watermarkPosition: 'bottom-right',
          watermarkOpacity: 80,
          displayOrder: 0,
          format: contentFormat || 'custom'
        };

        // Pass user.id if available, but it's optional now
        await contentService?.create(newContent, user?.id);
      }

      // Refresh content list
      await loadContent();
    } catch (err) {
      setError(err?.message || 'Erro ao fazer upload');
    }
  };

  const handleBulkAction = (action, data) => {
    if (action === 'change-category') {
      setContentItems(prev => 
        prev?.map(item => 
          selectedItems?.includes(item?.id) 
            ? { ...item, category: data?.category }
            : item
        )
      );
    } else if (action === 'toggle-watermark') {
      setContentItems(prev => 
        prev?.map(item => 
          selectedItems?.includes(item?.id) 
            ? { ...item, watermark: data?.enabled }
            : item
        )
      );
    } else if (action === 'delete') {
      setContentItems(prev => 
        prev?.filter(item => !selectedItems?.includes(item?.id))
      );
      setSelectedItems([]);
    } else if (action === 'move-up') {
      setContentItems(prev => 
        prev?.map(item => 
          selectedItems?.includes(item?.id) && item?.displayOrder > 1
            ? { ...item, displayOrder: item?.displayOrder - 1 }
            : item
        )
      );
    } else if (action === 'move-down') {
      setContentItems(prev => 
        prev?.map(item => 
          selectedItems?.includes(item?.id)
            ? { ...item, displayOrder: item?.displayOrder + 1 }
            : item
        )
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>Gerenciar Conteúdo - UP MIDIA Admin</title>
        <meta name="description" content="Gerencie artes e vídeos da UP MIDIA com ferramentas avançadas de edição e organização" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        
        {error && (
          <div className="mx-4 mt-4 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Carregando conteúdo...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="px-4 md:px-6 lg:px-10 py-6 md:py-8">
              <div className="mb-6 md:mb-8">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div>
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-primary mb-2">
                      Gerenciar Conteúdo
                    </h1>
                    <p className="text-sm md:text-base text-muted-foreground">
                      Organize, edite e faça upload de artes e vídeos
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="outline"
                      iconName="Download"
                      onClick={() => alert('Exportação em desenvolvimento')}
                    >
                      Exportar
                    </Button>
                    <Button
                      variant="default"
                      iconName="Upload"
                      onClick={() => setIsUploadModalOpen(true)}
                    >
                      Novo Upload
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className={`lg:col-span-7 ${showMobileEditor ? 'hidden lg:block' : 'block'}`}>
                  <ContentLibrary
                    contentItems={contentItems}
                    selectedItems={selectedItems}
                    onSelectItem={handleSelectItem}
                    onSelectAll={handleSelectAll}
                    onItemClick={handleItemClick}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onContentUpdate={loadContent}
                  />
                </div>

                <div className={`lg:col-span-5 ${showMobileEditor ? 'block' : 'hidden lg:block'}`}>
                  <ContentEditor
                    selectedItem={selectedItem}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onDelete={handleDeleteItem}
                  />
                </div>
              </div>
            </div>

            <BulkActionsBar
              selectedCount={selectedItems?.length}
              onBulkAction={handleBulkAction}
              onClearSelection={() => setSelectedItems([])}
            />

            <UploadModal
              isOpen={isUploadModalOpen}
              onClose={() => setIsUploadModalOpen(false)}
              onUpload={handleUpload}
            />
          </>
        )}
      </div>
    </>
  );
};

export default ContentManagement;